import { Loader2, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { money } from "@/lib/format";

const SLIDERS = [
  ["retAge", "Retirement age", 55, 75, 1, (v) => `${v}`],
  ["save", "Extra monthly savings", 0, 3000, 50, null],
  ["life", "Extra life coverage", 0, 2000000, 25000, null],
  ["ci", "Extra critical illness", 0, 500000, 5000, null],
  ["di", "Extra disability / month", 0, 5000, 100, null],
];

const Delta = ({ label, before, after, m, goodUp, testId }) => {
  const changed = Math.round(before) !== Math.round(after);
  const better = goodUp ? after > before : after < before;
  return (
    <div className="flex items-center justify-between gap-2 py-1 text-xs">
      <span className="text-slate-600">{label}</span>
      <span className="font-mono" data-testid={testId}>
        <span className="text-slate-400">{m(before)}</span> → <b className={!changed ? "text-slate-700" : better ? "text-emerald-600" : "text-rose-600"}>{m(after)}</b>
      </span>
    </div>
  );
};

export const WhatIfPanel = ({ base, s, sc, setSc, changed, onReset, onApply, applying, cur }) => {
  const m = (v) => money(v, cur);
  return (
    <aside className="whatif" data-testid="whatif-panel">
      <h3 className="font-heading text-base font-extrabold text-slate-900">What-if scenario</h3>
      <p className="mb-3 text-[11px] text-slate-500">Move the sliders to see results change live. Nothing is saved unless you apply it.</p>
      <div className="space-y-4">
        {SLIDERS.map(([k, label, min, max, step, fmt]) => (
          <div key={k} className="space-y-1.5">
            <div className="flex justify-between text-xs"><span className="font-semibold text-slate-700">{label}</span><span className="font-mono font-bold text-[#0072C6]" data-testid={`whatif-${k}-value`}>{fmt ? fmt(sc[k]) : m(sc[k])}</span></div>
            <Slider data-testid={`whatif-${k}-slider`} min={min} max={max} step={step} value={[sc[k]]} onValueChange={([v]) => setSc({ ...sc, [k]: v })} />
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="whatif-compare">
        <p className="field-label mb-1">Before → after</p>
        <Delta label="Retirement surplus / mo" before={base.ret.monthlySurplus} after={s.ret.monthlySurplus} m={m} goodUp testId="whatif-delta-retirement" />
        <Delta label="Life insurance gap" before={base.ins.client.lifeAdditional} after={s.ins.client.lifeAdditional} m={m} testId="whatif-delta-life" />
        <Delta label="Disability gap / mo" before={base.ins.client.diShortfall} after={s.ins.client.diShortfall} m={m} testId="whatif-delta-di" />
        <Delta label="Critical illness gap" before={base.ins.client.ciAdditional} after={s.ins.client.ciAdditional} m={m} testId="whatif-delta-ci" />
      </div>
      <div className="mt-3 flex gap-2">
        <Button variant="outline" size="sm" onClick={onReset} disabled={!changed} data-testid="whatif-reset-button" className="gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Reset</Button>
        <Button size="sm" onClick={onApply} disabled={!changed || applying} data-testid="whatif-apply-button" className="flex-1 gap-1.5">
          {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Apply to client
        </Button>
      </div>
      <p className="mt-2 text-[10.5px] text-slate-500">Apply saves the retirement age, adds extra savings as an "Other savings" retirement account, and adds the extra coverage to the client's existing policies.</p>
    </aside>
  );
};
